import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.regexp.exec.js";
import "core-js/modules/es.string.pad-end.js";
import "core-js/modules/es.string.replace.js";
import "core-js/modules/es.string.search.js";
import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import queryparse from 'tricks/string/queryparse';
import createUrl from 'tricks/string/createUrl';
import CenterContent from 'components/CenterContent';
import Stack from 'components/Stack';
import Button from 'components/Button';
import Landmark from 'components/Landmark';
import Text from 'components/Text';
import { useVerifyMyEmail } from 'actions/userEmails';
import { useAuthContext } from '../../contexts/auth';
import AlertRenderer from '../../shared/alertRenderer';

// Reads the `email` claim out of the JWT's payload segment without
// verifying the signature - the server independently verifies it, this is
// only used to know what to display/submit. Not sensitive: JWT payloads are
// base64url-encoded, not encrypted, so nothing is exposed that the link
// itself doesn't already carry.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
function decodeEmailFromToken(token) {
  try {
    var _token$split = token.split('.'),
      _token$split2 = _slicedToArray(_token$split, 2),
      payload = _token$split2[1];
    if (!payload) {
      return undefined;
    }
    var base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    var padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
    var json = atob(padded);
    return JSON.parse(json).email;
  } catch (_unused) {
    return undefined;
  }
}

// Public route (no login required) - the link in the verification email
// points here. `authUser` is read directly from context rather than via a
// `<Page>`/private-route wrapper, since this page must render its result for
// a logged-out visitor too, not redirect them to log in first.
function VerifyEmail() {
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var location = useLocation();
  var _useAuthContext = useAuthContext(),
    authUser = _useAuthContext.authUser;
  var _queryparse = queryparse(location.search),
    token = _queryparse.token;
  // `email` is deliberately not a URL param - Mailgun's click-tracking
  // mangles links containing an "@", so it's carried inside the JWT instead.
  var email = token ? decodeEmailFromToken(token) : undefined;
  var _useVerifyMyEmail = useVerifyMyEmail(),
    verifyMyEmail = _useVerifyMyEmail.verifyMyEmail,
    isPending = _useVerifyMyEmail.isPending,
    isSuccess = _useVerifyMyEmail.isSuccess,
    isError = _useVerifyMyEmail.isError;
  var hasVerified = useRef(false);
  useEffect(function () {
    if (!email || !token || hasVerified.current) {
      return;
    }
    hasVerified.current = true;
    verifyMyEmail({
      token: token
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, token]);
  var isMissingParams = !email || !token;
  var button = authUser ? /*#__PURE__*/_jsx(Button, {
    as: Link,
    to: "/settings",
    variant: "primary",
    children: t('actions_go_to_settings')
  }) : /*#__PURE__*/_jsx(Button, {
    as: Link,
    to: createUrl('/login', {
      redirectAfterLogin: '/settings'
    }),
    variant: "primary",
    children: t('back_to_login')
  });
  var emailField = email ? /*#__PURE__*/_jsxs(Text, {
    bold: true,
    children: [t('email_colon'), " ", email]
  }) : null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(CenterContent, {
      height: "90vh",
      contentWidth: 440,
      children: [isPending && /*#__PURE__*/_jsx(Landmark, {
        title: t('loading'),
        icon: "spinner"
      }), isSuccess && /*#__PURE__*/_jsx(Landmark, {
        icon: "ok",
        title: t('verify_email_page_title_success'),
        children: /*#__PURE__*/_jsxs(Stack, {
          spacing: "m",
          children: [/*#__PURE__*/_jsxs(Stack, {
            spacing: "xs",
            children: [/*#__PURE__*/_jsx(Text, {
              children: t('verify_email_page_success')
            }), emailField]
          }), button]
        })
      }), (isMissingParams || isError) && /*#__PURE__*/_jsx(Landmark, {
        icon: "flag",
        title: t('verify_email_page_title_error'),
        children: /*#__PURE__*/_jsxs(Stack, {
          spacing: "m",
          children: [/*#__PURE__*/_jsxs(Stack, {
            spacing: "xs",
            children: [/*#__PURE__*/_jsx(Text, {
              children: t('verify_email_page_error_1')
            }), /*#__PURE__*/_jsx(Text, {
              children: t('verify_email_page_error_2')
            }), emailField]
          }), button]
        })
      })]
    }), /*#__PURE__*/_jsx(AlertRenderer, {})]
  });
}
export default VerifyEmail;